import { TailSpin } from "react-loader-spinner";

import { Card } from "../../../../shared/card/card.tsx";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import type { TimesheetStatsResponse } from "../../../timesheet/types.ts";
import { OverviewSummaryCards } from "../../../timesheet/tabs/overview-tab/components/overview-summary-cards/overview-summary-cards.tsx";
import { ProjectAllocationCards } from "../../../timesheet/tabs/overview-tab/components/project-allocation-cards/project-allocation-cards.tsx";
import { TimeBreakdownCards } from "../../../timesheet/tabs/overview-tab/components/time-breakdown-cards/time-breakdown-cards.tsx";
import type { PeriodRange } from "../../types.ts";

import styles from "./overview-content.module.css";

export interface OverviewContentProps {
  range: PeriodRange;
  loading: boolean;
  error: string | null;
  data: TimesheetStatsResponse | null;
  employeeId?: string;
}

export function OverviewContent({
  range,
  loading,
  error,
  data,
  employeeId,
}: OverviewContentProps) {
  /** Custom tab selected but date range not chosen yet (avoid "No records" before fetch). */
  const customRangeIncomplete =
    range.type === "CUSTOM" &&
    (!range.startDate || !range.endDate);

  if (customRangeIncomplete) {
    return (
      <Card padding="3xl" className={styles.stateCard}>
        <EmptyStateMessage message="Please specify a date range to view the overview." />
      </Card>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        <TailSpin
          height="80"
          width="80"
          color="var(--primary-color)"
          visible
        />
        <p className={styles.loadingText}>Loading overview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card padding="3xl" className={styles.stateCard}>
        <EmptyStateMessage message={error} className={styles.errorEmpty} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card padding="3xl" className={styles.stateCard}>
        <EmptyStateMessage />
      </Card>
    );
  }

  return (
    <div className={styles.container}>
      <OverviewSummaryCards
        totalTime={data.totalTime || "00:00"}
        targetHours={data.targetHours || 0}
        projectCount={data.projectOverview?.length || 0}
        utilization={data.utilization || 0}
      />
      <TimeBreakdownCards breakdowns={data.timeBreakdown || []} />
      <ProjectAllocationCards
        projects={data.projectOverview || []}
        employeeId={employeeId}
        showActions
      />
    </div>
  );
}
